import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import { useToast } from '../hooks/use-toast';
import { ShieldAlert, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';

/**
 * Mandatory password change modal shown whenever the authenticated user's
 * `must_change_password` flag is true (seeded/rotated accounts and admin
 * password resets). The modal is not dismissable — the only escapes are
 * completing the change or signing out.
 */
const RULES = [
  { key: 'len', label: 'At least 10 characters', test: (v) => v.length >= 10 },
  { key: 'lower', label: 'One lowercase letter', test: (v) => /[a-z]/.test(v) },
  { key: 'upper', label: 'One UPPERCASE letter', test: (v) => /[A-Z]/.test(v) },
  { key: 'digit', label: 'One digit', test: (v) => /\d/.test(v) },
  { key: 'symbol', label: 'One symbol', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

export const ForcePasswordResetModal = () => {
  const { user, logout, refreshMe } = useAuth();
  const { toast } = useToast();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const open = !!user?.must_change_password;

  const strength = useMemo(() => {
    const passed = RULES.filter((r) => r.test(newPw));
    // 3 of 4 char-class requirement + length
    const classes = passed.filter((r) => r.key !== 'len').length;
    return {
      passed: passed.map((r) => r.key),
      meetsPolicy: newPw.length >= 10 && classes >= 3,
    };
  }, [newPw]);

  const canSubmit = currentPw && strength.meetsPolicy && newPw === confirmPw && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post('/auth/change-password', { current_password: currentPw, new_password: newPw });
      toast({ title: 'Password updated', description: 'You can now continue using ESD.' });
      await refreshMe();
    } catch (e) {
      toast({ title: 'Password change failed', description: e.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* not dismissable */ }}>
      <DialogContent
        className="max-w-md [&>button.absolute]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="force-password-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[hsl(217,91%,24%)]">
            <ShieldAlert className="h-5 w-5" /> Update your password
          </DialogTitle>
          <DialogDescription>
            Your current password was set by an administrator or is a rotated default. Please choose a new personal password to continue using ESD.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="fpr-current">Current password</Label>
            <div className="relative">
              <Input
                id="fpr-current"
                type={showCur ? 'text' : 'password'}
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                autoFocus
                data-testid="force-password-current-input"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                onClick={() => setShowCur((v) => !v)}
                aria-label="Toggle current password visibility"
              >
                {showCur ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="fpr-new">New password</Label>
            <div className="relative">
              <Input
                id="fpr-new"
                type={showNew ? 'text' : 'password'}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                data-testid="force-password-new-input"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                onClick={() => setShowNew((v) => !v)}
                aria-label="Toggle new password visibility"
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="fpr-confirm">Confirm new password</Label>
            <Input
              id="fpr-confirm"
              type={showNew ? 'text' : 'password'}
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              data-testid="force-password-confirm-input"
            />
            {confirmPw && confirmPw !== newPw && (
              <div className="mt-1 text-xs text-red-600">Passwords do not match.</div>
            )}
          </div>

          <ul className="text-xs space-y-1 rounded-md border border-slate-200 bg-slate-50 p-3">
            {RULES.map((r) => {
              const ok = strength.passed.includes(r.key);
              return (
                <li key={r.key} className={`flex items-center gap-1.5 ${ok ? 'text-emerald-700' : 'text-slate-500'}`}>
                  {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                  {r.label}
                </li>
              );
            })}
            <li className="text-[11px] text-slate-500 pt-1 border-t border-slate-200 mt-1">
              Policy requires length ≥ 10 <span className="font-semibold">and</span> at least 3 of the 4 character classes.
            </li>
          </ul>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={logout} data-testid="force-password-signout-btn">
            Sign out
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]"
            data-testid="force-password-submit-btn"
          >
            {submitting ? 'Updating…' : 'Update password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ForcePasswordResetModal;
